import ErrorHandler from 'store/utils/ErrorHandler';
import Notify from 'store/utils/Notify';
import { getEntity } from 'store/services/entities/selectors';
import * as api from './api';
import * as types from './actionTypes';
import { getPaginationOptions } from './selectors';

/**
 * Get paginated Entities Features
 *
 * @param params
 */
export const getEntitiesFeaturesList = (params = {}) => async (
  dispatch,
  getState,
) => {
  try {
    dispatch({
      type: types.GET_ENTITY_FEATURE_LIST_REQUEST,
    });

    const finalParams = {
      ...getPaginationOptions(getState()),
      ...params,
    };

    dispatch({
      type: types.UPDATE_ENTITY_FEATURE_LIST_PAGINATION_OPTIONS,
      payload: {
        paginationOptions: finalParams,
      },
    });

    const entityId = getEntity(getState()).id;

    const response = await api.getEntitiesFeaturesRequest(
      entityId,
      finalParams,
    );

    dispatch({
      type: types.GET_ENTITY_FEATURE_LIST_SUCCESS,
      payload: {
        items: response.data.results,
        count: response.data.count,
      },
    });
  } catch (error) {
    dispatch({
      type: types.GET_ENTITY_FEATURE_LIST_ERROR,
      payload: {
        error: error.message,
      },
    });

    Notify.error({
      title: 'An error occurred',
      description: ErrorHandler.getNotificationError(error),
    });
  }
};

/**
 * Add an Entity Feature
 *
 * @param data
 */
export const addEntitiesFeature = data => async (dispatch, getState) => {
  try {
    dispatch({
      type: types.ADD_ENTITY_FEATURE_REQUEST,
    });

    const entityId = getEntity(getState()).id;

    await api.addEntitiesFeatureRequest(entityId, data);

    dispatch({
      type: types.ADD_ENTITY_FEATURE_SUCCESS,
    });

    const finalParams = {
      ...getPaginationOptions(getState()),
      page: 1,
    };

    dispatch({
      type: types.UPDATE_ENTITY_FEATURE_LIST_PAGINATION_OPTIONS,
      payload: {
        paginationOptions: finalParams,
      },
    });

    const response = await api.getEntitiesFeaturesRequest(
      entityId,
      finalParams,
    );

    dispatch({
      type: types.GET_ENTITY_FEATURE_LIST_SUCCESS,
      payload: {
        items: response.data.results,
        count: response.data.count,
      },
    });

    Notify.success({
      title: 'Entity Feature was created successfully',
    });
  } catch (error) {
    dispatch({
      type: types.ADD_ENTITY_FEATURE_ERROR,
      payload: {
        error: error.message,
      },
    });

    Notify.error({
      title: 'Fail to create Entity Feature',
      description: ErrorHandler.getNotificationError(error),
    });
  }
};

/**
 * Multi Change Entity Feature (create/update/delete)
 *
 * @param data
 */
export const multiEntitiesFeature = (entityId, data) => async (
  dispatch,
  getState,
) => {
  try {
    dispatch({
      type: types.UPDATE_ENTITY_FEATURE_REQUEST,
    });

    const entityId = getEntity(getState()).id;

    await api.multiEntitiesFeatureRequest(entityId, data);

    dispatch({
      type: types.UPDATE_ENTITY_FEATURE_SUCCESS,
    });

    const finalParams = {
      ...getPaginationOptions(getState()),
      page: 1,
    };

    dispatch({
      type: types.UPDATE_ENTITY_FEATURE_LIST_PAGINATION_OPTIONS,
      payload: {
        paginationOptions: finalParams,
      },
    });

    const response = await api.getEntitiesFeaturesRequest(
      entityId,
      finalParams,
    );

    dispatch({
      type: types.GET_ENTITY_FEATURE_LIST_SUCCESS,
      payload: {
        items: response.data.results,
        count: response.data.count,
      },
    });
  } catch (error) {
    dispatch({
      type: types.UPDATE_ENTITY_FEATURE_ERROR,
      payload: {
        error: error.message,
      },
    });

    Notify.error({
      title: 'An error occurred',
      description: ErrorHandler.getNotificationError(error),
    });
  }
};

/**
 * Get an Entity Feature
 *
 * @param id
 */
export const getEntitiesFeature = id => async (dispatch, getState) => {
  try {
    dispatch({
      type: types.GET_ENTITY_FEATURE_REQUEST,
    });

    const entityId = getEntity(getState()).id;

    const { data } = await api.getEntitiesFeatureRequest(entityId, id);

    dispatch({
      type: types.GET_ENTITY_FEATURE_SUCCESS,
      payload: {
        item: data,
      },
    });
  } catch (error) {
    dispatch({
      type: types.GET_ENTITY_FEATURE_ERROR,
      payload: {
        error: error.message,
      },
    });

    Notify.error({
      title: 'An error occurred',
      description: ErrorHandler.getNotificationError(error),
    });
  }
};

/**
 * Update an Entity Feature
 *
 * @param id
 * @param data
 */
export const updateEntitiesFeature = (id, data) => async (
  dispatch,
  getState,
) => {
  try {
    dispatch({
      type: types.UPDATE_ENTITY_FEATURE_REQUEST,
    });

    const entityId = getEntity(getState()).id;

    await api.updateEntitiesFeatureRequest(entityId, id, data);

    dispatch({
      type: types.UPDATE_ENTITY_FEATURE_SUCCESS,
    });

    const finalParams = {
      ...getPaginationOptions(getState()),
      page: 1,
    };

    dispatch({
      type: types.UPDATE_ENTITY_FEATURE_LIST_PAGINATION_OPTIONS,
      payload: {
        paginationOptions: finalParams,
      },
    });

    const response = await api.getEntitiesFeaturesRequest(
      entityId,
      finalParams,
    );

    dispatch({
      type: types.GET_ENTITY_FEATURE_LIST_SUCCESS,
      payload: {
        items: response.data.results,
        count: response.data.count,
      },
    });

    Notify.success({
      title: 'Entity Feature was updated successfully',
    });
  } catch (error) {
    dispatch({
      type: types.UPDATE_ENTITY_FEATURE_ERROR,
      payload: {
        error: error.message,
      },
    });

    Notify.error({
      title: 'Fail to update Entity Feature',
      description: ErrorHandler.getNotificationError(error),
    });
  }
};

/**
 * Delete an Entities Feature
 *
 * @param id
 */
export const deleteEntitiesFeature = id => async (dispatch, getState) => {
  try {
    dispatch({
      type: types.DELETE_ENTITY_FEATURE_REQUEST,
    });

    const entityId = getEntity(getState()).id;

    await api.deleteEntitiesFeatureRequest(entityId, id);

    dispatch({
      type: types.DELETE_ENTITY_FEATURE_SUCCESS,
    });

    const finalParams = {
      ...getPaginationOptions(getState()),
      page: 1,
    };

    dispatch({
      type: types.UPDATE_ENTITY_FEATURE_LIST_PAGINATION_OPTIONS,
      payload: {
        paginationOptions: finalParams,
      },
    });

    const { data } = await api.getEntitiesFeaturesRequest(
      entityId,
      finalParams,
    );

    dispatch({
      type: types.GET_ENTITY_FEATURE_LIST_SUCCESS,
      payload: {
        items: data.results,
        count: data.count,
      },
    });

    Notify.success({
      title: 'Entity Feature was deleted successfully',
    });
  } catch (error) {
    dispatch({
      type: types.DELETE_ENTITY_FEATURE_ERROR,
      payload: {
        error: error.message,
      },
    });

    Notify.error({
      title: 'Fail to delete Entity Feature',
      description: ErrorHandler.getNotificationError(error),
    });
  }
};
